import { Fragment, useEffect, useRef, useState } from "react";
import { TbDotsVertical } from "react-icons/tb";
import { BsBookmark } from "react-icons/bs";
import { useLocation, useNavigate } from "react-router-dom";
import VideoCard from "../../common/VideoCard";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { FaFolder, FaTrash } from "react-icons/fa";
import useSelectVideo from "../../../hooks/useSelectVideo";
import FolderImage from "../../../assets/icons/common/SavedFolder.png";
import SkeletonVideoCard from "../../skeleton/SkeletonVideoCard";
import { SavedModal } from "../../Modals/account/SavedTab";
import { SavedVideosById } from "../../../redux/ApiSlice/HomePageVideoSlice";
import MoveListToFolder from "../../Modals/account/MoveListToFolder";
import { bulkUnsavePosts, deleteSavedVideo, getSavedFolders, savedVideoList } from "../../../redux/ApiSlice/SavedVideosSlice";

export function SavedTab({ SavedFolderList, loading, loadingMore, handleOpenEditModal, handleOpenDeleteModal, handleCreateFolder, handleRenameFolder, nextPage, showFolders, setShowFolders, onMoveDrawerToggle }) {
    const menuRef = useRef(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [savedId, setSavedId] = useState(null);
    const location = useLocation();
    const [show, setShow] = useState(false);
    const [showFolder, setShowFolder] = useState(showFolders || false);
    const [showtext, setsShowText] = useState("");
    const { SavedVideos, totalCount: savedTotalCount } = useSelector((state) => state.savedVideos);
    const { User_profile } = useSelector((state) => state.AccountProfile);
    const selectVideo = useSelectVideo();

    const [moveModalOpen, setMoveModalOpen] = useState(false);
    const [selectedPostId, setSelectedPostId] = useState(null);
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedVideos, setSelectedVideos] = useState([]);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState("");
    const [isCreating, setIsCreating] = useState(false);
    const [createError, setCreateError] = useState("");
    const [isRenameOpen, setIsRenameOpen] = useState(false);
    const [renameFolderId, setRenameFolderId] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const [renameError, setRenameError] = useState("");

    const handleClickOutside = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
            setSavedId(null);
        }
    };

    const handleOpen = (val) => {
        setShow(!show);
        setsShowText(val);
    };

    const handleNavigate = (value) => {
        if (value) navigate(`/account/saved/folder/${value}`);
    };

    // Delete video handler
    const deleteFolderVideo = async (postId) => {
        await dispatch(deleteSavedVideo({ folder_id: 'saved', post_id: postId }));
    };

    const removeVideoFromlist = async (postId) => {
        try {
            // Wait for the toggle action to complete
            await dispatch(SavedVideosById(postId)).unwrap();
        } catch (error) {
            console.error("Error removing video:", error);
        }
    }

    const fetchData = async () => {
        await dispatch(savedVideoList({ nextUrl: null, isLoadMore: false }));
        if (User_profile?.id) {
            await dispatch(getSavedFolders(User_profile.id));
        }
    };

    const handleSelectVideo = async (id) => {
        selectVideo(id, SavedVideos, location.pathname, 'saved', nextPage);
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dispatch]);

    // Sync showFolder with parent's showFolders prop
    useEffect(() => {
        if (showFolders !== undefined) {
            setShowFolder(showFolders);
        }
    }, [showFolders]);

    // Update parent when showFolder changes
    useEffect(() => {
        if (setShowFolders) {
            setShowFolders(showFolder);
        }
    }, [showFolder, setShowFolders]);


    const handleMoveVideo = (postId) => {
        setSelectedPostId(postId);
        setMoveModalOpen(true);
        if (onMoveDrawerToggle) onMoveDrawerToggle(true);
    }

    const handleEnterSelectionMode = (postId) => {
        setSelectionMode(true);
        setSelectedVideos([postId]);
    };

    const handleToggleVideoSelection = (postId) => {
        setSelectedVideos(prev => {
            if (prev.includes(postId)) {
                const newSelection = prev.filter(id => id !== postId);
                // Exit selection mode if no videos are selected
                if (newSelection.length === 0) {
                    setSelectionMode(false);
                }
                return newSelection;
            } else {
                return [...prev, postId];
            }
        });
    };

    const handleCancelSelection = () => {
        setSelectionMode(false);
        setSelectedVideos([]);
    };

    const handleDeleteSelected = async () => {
        await dispatch(bulkUnsavePosts(selectedVideos));
        handleCancelSelection();
    };

    const handleMoveSelected = () => {
        if (selectedVideos.length === 0) return;
        setSelectedPostId(selectedVideos.length === 1 ? selectedVideos[0] : null);
        setMoveModalOpen(true);
        if (onMoveDrawerToggle) onMoveDrawerToggle(true);
    };

    const closeMoveModal = () => {
        setMoveModalOpen(false);
        if (onMoveDrawerToggle) onMoveDrawerToggle(false);
    };

    useEffect(() => {
        if (onMoveDrawerToggle) {
            onMoveDrawerToggle(moveModalOpen);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moveModalOpen]);

    const openCreateSection = () => {
        setIsCreateOpen(true);
        setIsRenameOpen(false);
        setCreateError("");
    };

    const openRenameSection = (folder) => {
        setIsCreateOpen(false);
        setIsRenameOpen(true);
        setRenameFolderId(folder?.id ?? null);
        setRenameValue(folder?.name ?? "");
        setRenameError("");
        setSavedId(null);
    };

    const cancelRename = () => {
        setIsRenameOpen(false);
        setRenameFolderId(null);
        setRenameValue("");
        setRenameError("");
    };

    const cancelCreate = () => {
        setIsCreateOpen(false);
        setNewFolderName("");
        setCreateError("");
    };

    const submitCreate = async () => {
        const trimmedName = newFolderName.trim();
        if (!trimmedName) {
            setCreateError("Folder name is required");
            return;
        }
        if (!handleCreateFolder) return;
        try {
            setIsCreating(true);
            await handleCreateFolder(trimmedName);
            setNewFolderName("");
            setIsCreateOpen(false);
        } finally {
            setIsCreating(false);
        }
    };

    const submitRename = async () => {
        const trimmedName = renameValue.trim();
        if (!trimmedName) {
            setRenameError("Folder name is required");
            return;
        }
        if (!handleRenameFolder || !renameFolderId) return;
        await handleRenameFolder(trimmedName, renameFolderId);
        cancelRename();
    };

    return (
        <div className="relative h-full min-h-screen">
            {loading &&
                <SkeletonVideoCard />
            }

            {!loading &&
                <>
                    {/* Toggle buttons - always visible */}
                    <div className="w-full flex justify-center sticky top-0 bg-white dark:bg-[#1E1D21] z-10">
                        <div className="flex items-center bg-[#DFE6EA] rounded-full relative">
                            <motion.div
                                className="absolute bg-[#0A96C1] rounded-full h-full"
                                initial={false}
                                animate={{
                                    x: showFolder ? '100%' : '0%',
                                    width: '6rem'
                                }}
                                transition={{
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 30
                                }}
                            />
                            <button
                                onClick={() => setShowFolder(false)}
                                className={`w-[6rem] py-1 text-[14px] rounded-full relative z-10 transition-colors duration-300 ${!showFolder ? 'text-white' : 'text-black'
                                    }`}
                            >
                                All
                            </button>
                            <button
                                onClick={() => setShowFolder(true)}
                                className={`w-[6rem] py-1 text-[14px] rounded-full relative z-10 transition-colors duration-300 ${showFolder ? 'text-white' : 'text-black'
                                    }`}
                            >
                                Folders
                            </button>
                        </div>
                    </div>

                    {showFolder &&
                        <>
                            {/* Inline create folder action */}
                            <div className="px-3 pt-3">
                                {!isCreateOpen && !isRenameOpen && (
                                    <button
                                        onClick={openCreateSection}
                                        className="w-full bg-[#BDE6FF] font-semibold text-sm py-2 rounded-md shadow-sm"
                                    >
                                        Create Folder
                                    </button>
                                )}

                                {isCreateOpen && !isRenameOpen && (
                                    <div className="mt-3 bg-[#BDE6FF] rounded-lg px-3 py-3 shadow-sm">
                                        <p className="text-xs font-semibold text-gray-800 mb-2">Folder Name</p>
                                        <input
                                            type="text"
                                            value={newFolderName}
                                            onChange={(e) => {
                                                setNewFolderName(e.target.value);
                                                setCreateError("");
                                            }}
                                            placeholder="Type folder name"
                                            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#333333] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#0A96C1]"
                                        />
                                        {createError && (
                                            <p className="text-[11px] text-red-600 mt-1">{createError}</p>
                                        )}
                                        <div className="flex justify-center gap-5 mt-3">
                                            <button
                                                onClick={cancelCreate}
                                                className="px-3 py-1 text-sm rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-transparent"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={submitCreate}
                                                disabled={isCreating}
                                                className={`px-4 py-1 text-sm rounded-xl font-semibold text-white ${isCreating ? "bg-[#0A96C1]/60 cursor-not-allowed" : "bg-[#0A96C1] hover:bg-[#087fa8]"}`}
                                            >
                                                {isCreating ? "Creating..." : "Create"}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {isRenameOpen && (
                                    <div className="mt-3 bg-[#BDE6FF] rounded-lg px-3 py-3 shadow-sm">
                                        <p className="text-xs font-semibold text-gray-800 mb-2">Rename Folder</p>
                                        <input
                                            type="text"
                                            value={renameValue}
                                            onChange={(e) => {
                                                setRenameValue(e.target.value);
                                                setRenameError("");
                                            }}
                                            placeholder="Type new folder name"
                                            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#333333] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#0A96C1]"
                                        />
                                        {renameError && (
                                            <p className="text-[11px] text-red-600 mt-1">{renameError}</p>
                                        )}
                                        <div className="flex justify-center gap-5 mt-3">
                                            <button
                                                onClick={cancelRename}
                                                className="px-3 py-1 text-sm rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-transparent"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={submitRename}
                                                className="px-4 py-1 text-sm rounded-xl font-semibold text-white bg-[#0A96C1] hover:bg-[#087fa8]"
                                            >
                                                Save
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Folder list */}
                            {SavedFolderList?.data?.length ? (
                                <div className="h-[90%] md:h-[86%] p-3 overflow-y-auto no-scrollbar mt-1">
                                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-4">
                                        {SavedFolderList?.data?.map((item) => {
                                            const previewImage = item?.videos?.[0]?.thumbnail_url || FolderImage;
                                            const videoCount = item?.videos?.length || 0;
                                            return (
                                                <div key={item.id} className="space-y-1">
                                                    <div
                                                        className="relative h-56 rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-800 shadow-sm cursor-pointer group"
                                                        onClick={() => handleNavigate(item.id)}
                                                    >
                                                        <img
                                                            src={previewImage}
                                                            alt={item.name}
                                                            className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                                                        />

                                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" />

                                                        <div className="absolute top-2 left-2 bg-black/70 text-white text-[11px] px-2 py-1 rounded-full flex items-center gap-1">
                                                            <span>{videoCount}</span>
                                                            <BsBookmark size={10} />
                                                        </div>

                                                        <button
                                                            className="absolute top-2 right-2 text-white bg-black/60 rounded-full p-1 hover:bg-black/80"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSavedId(item.id === savedId ? null : item.id);
                                                            }}
                                                        >
                                                            <TbDotsVertical size={16} />
                                                        </button>

                                                        {savedId === item.id && (
                                                            <div
                                                                ref={menuRef}
                                                                className="absolute right-2 top-10 bg-white dark:bg-[#333333] border border-gray-300 dark:border-gray-700 z-30 overflow-hidden rounded-md shadow-md"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <p
                                                                    onClick={() => handleOpenDeleteModal(item.id)}
                                                                    className="flex items-center text-[12px] text-black dark:text-white cursor-pointer px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 border-b border-gray-200 dark:border-gray-800"
                                                                >
                                                                    Delete
                                                                </p>
                                                                <p
                                                                    onClick={() => openRenameSection(item)}
                                                                    className="flex items-center text-[12px] text-black dark:text-white cursor-pointer px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800"
                                                                >
                                                                    Rename
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="flex items-center justify-between px-1">
                                                        <p className="text-[11px] font-semibold text-black dark:text-white truncate">
                                                            {item.name}
                                                        </p>
                                                        {/* <p className="text-[11px] text-gray-500 dark:text-gray-400">{videoCount} items</p> */}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex justify-center items-center">
                                    <p className="text-[12px] mt-5 text-gray-400 dark:text-gray-500">
                                        No folder created yet.
                                    </p>
                                </div>
                            )}
                        </>
                    }

                    {/* General saved videos view */}
                    <div className="relative">
                        {!showFolder &&
                            <div className="overflow-y-scroll">
                                {savedTotalCount > 0 && (
                                    <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 px-3 pt-2">
                                        {savedTotalCount} {savedTotalCount === 1 ? "Video" : "Videos"}
                                    </p>
                                )}
                                <div className="grid grid-cols-3 p-2 gap-1">
                                    {SavedVideos.length > 0 && Array.isArray(SavedVideos) ? (
                                        SavedVideos.map((item) => (
                                            <VideoCard
                                                key={item.id}
                                                id={item.id}
                                                belowDecs={true}
                                                threeDots={true}
                                                isSavedView={true}
                                                isSavedList={true}
                                                video={item.video_url}
                                                user_id={item.created_by.id}
                                                authorId={item.created_by.id}
                                                body={item.post_description}
                                                view_count={item.view_count}
                                                likes_count={item.likes_count}
                                                handleMoveVideo={handleMoveVideo}
                                                handleDeletePost={deleteFolderVideo}
                                                removeVideoFromlist={removeVideoFromlist}
                                                handleEnterSelectionMode={handleEnterSelectionMode}
                                                avatar_url={item.created_by.avatar_url}
                                                selectVideo={handleSelectVideo}
                                                thumbnail={item.thumbnail_url}
                                                selectionMode={selectionMode}
                                                isSelected={selectedVideos.includes(item.id)}
                                                onToggleSelection={handleToggleVideoSelection}
                                            />
                                        ))
                                    ) : (
                                        <div className="flex justify-center h-[10rem] col-span-12 items-center">
                                            <p className="text-[12px] mt-5 text-gray-600 dark:text-gray-500">
                                                You saved videos will appear here.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Loading more indicator */}
                                {loadingMore && (
                                    <div className="flex justify-center items-center py-4">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                                    </div>
                                )}
                            </div>
                        }

                        {/* Selection mode toolbar */}
                        <AnimatePresence>
                            {selectionMode && !showFolder && selectedVideos.length > 0 && !moveModalOpen && (
                                <motion.div
                                    initial={{ y: 100 }}
                                    animate={{ y: 0 }}
                                    exit={{ y: 100 }}
                                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                                    className="sticky bottom-0 inset-x-0 bg-white dark:bg-[#333333] border-t border-gray-300 dark:border-gray-700 shadow-lg z-50"
                                >
                                    <div className="flex items-center justify-between gap-x-2 p-1.5">
                                        <button
                                            onClick={handleMoveSelected}
                                            disabled={selectedVideos.length === 0}
                                            className="w-full flex justify-center p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-[#444444] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <FaFolder className="text-[16px] text-gray-700 dark:text-white" />
                                        </button>
                                        <button
                                            onClick={handleDeleteSelected}
                                            disabled={selectedVideos.length === 0}
                                            className="w-full flex justify-center p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-[#444444] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <FaTrash className="text-[16px] text-gray-700 dark:text-white" />
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    <SavedModal
                        size={show}
                        handleOpen={handleOpen}
                        showtext={showtext}
                    />
                </>
            }

            <MoveListToFolder
                loading={loading}
                isOpen={moveModalOpen}
                SavedFolderList={SavedFolderList}
                selectedPostId={selectedPostId}
                selectedPostIds={selectedVideos}
                handleClose={closeMoveModal}
                onMoveComplete={() => {
                    fetchData();
                    handleCancelSelection();
                }}
            />
        </div>
    );
}